import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  InputNumber,
  Button,
  Typography,
  Row,
  Col,
  message,
  Tag,
  Alert,
  Modal,
  Input,
} from "antd";
import { CheckCircleTwoTone } from "@ant-design/icons";
import CustomNumberInput from "../components/CustomNumberInput";
import { API_BASE_URL } from "../api/api";

const { Title, Text } = Typography;

const RateConfigForm = ({
  setKey,
  configData,
  isActive,
  isEditing,
  onSwitchSet,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const isPercentage = configData.config_type === "percentage";
  const isFixed = configData.config_type === "fixed";
  const [earnRate, setEarnRate] = useState(configData.earn_rate || 0);
  const [redeemRate, setRedeemRate] = useState(configData.redeem_rate || 0);
  const [cashOutRate, setCashOutRate] = useState(configData.cash_out_rate || 0);

  useEffect(() => {
    setEarnRate((configData.earn_rate || 0) * 100);
    setRedeemRate((configData.redeem_rate || 0) * 100);
    setCashOutRate((configData.cash_out_rate || 0) * 100);
  }, [configData]);

  useEffect(() => {
    form.resetFields();
  }, [configData, form]);

  // Transform values for display
  const transformedConfig = {
    ...configData,
    basic_adult: isPercentage
      ? configData.basic_adult * 100
      : configData.basic_adult,
    basic_child: isPercentage
      ? configData.basic_child * 100
      : configData.basic_child,
    gold_adult: isPercentage
      ? configData.gold_adult * 100
      : configData.gold_adult,
    gold_child: isPercentage
      ? configData.gold_child * 100
      : configData.gold_child,
    earn_rate: configData.earn_rate * 100,
    redeem_rate: configData.redeem_rate * 100,
    cash_out_rate: configData.cash_out_rate * 100,
  };

  return (
    <Card
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Text strong>
            {setKey === "set1" ? "Config Set A" : "Config Set B"}
          </Text>
          <Tag color={configData?.config_type === "fixed" ? "volcano" : "blue"}>
            {(configData?.config_type || "").toUpperCase()}
          </Tag>
          {isActive && (
            <CheckCircleTwoTone
              twoToneColor="#52c41a"
              title="Currently Active"
            />
          )}
        </div>
      }
      bordered
      style={{
        position: "relative",
        opacity: isEditing ? 1 : 0.5,
        border: isActive ? "2px solid #52c41a" : "1px dashed #ccc",
        backgroundColor: isEditing ? "#fff" : "#fafafa",
      }}
      extra={
        !isEditing && (
          <Button type="primary" onClick={() => onSwitchSet(setKey)}>
            Switch to View/Edit
          </Button>
        )
      }
    >
      {isActive && (
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            backgroundColor: "#52c41a",
            color: "#fff",
            padding: "2px 8px",
            fontSize: 12,
            borderBottomLeftRadius: 4,
          }}
        >
          Active
        </div>
      )}

      {isEditing && (
        <Alert
          type={isActive ? "success" : "warning"}
          message={
            isActive
              ? `This configuration is currently active (${configData.config_type.toUpperCase()})`
              : `You are editing this config, but it's not active until you save it`
          }
          style={{ marginBottom: 20 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={transformedConfig}
        onFinish={(values) => onSubmit(values, setKey)}
      >
        <Title level={5}>Basic Plan</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={isFixed ? "Adult Discount" : "Adult Rate"}
              name="basic_adult"
            >
              <CustomNumberInput
                isFixed={isFixed}
                isPercentage={isPercentage}
                isEditing={isEditing}
                value={form.getFieldValue("basic_adult")}
                onChange={(val) => form.setFieldsValue({ basic_adult: val })}
                min={0}
                step={1}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={isFixed ? "Child Discount" : "Child Rate"}
              name="basic_child"
            >
              <CustomNumberInput
                isFixed={isFixed}
                isPercentage={isPercentage}
                isEditing={isEditing}
                value={form.getFieldValue("basic_child")}
                onChange={(val) => form.setFieldsValue({ basic_child: val })}
                min={0}
                step={1}
              />
            </Form.Item>
          </Col>
        </Row>

        <Title level={5}>Gold Plan</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={isFixed ? "Adult Discount" : "Adult Rate"}
              name="gold_adult"
            >
              <CustomNumberInput
                isFixed={isFixed}
                isPercentage={isPercentage}
                isEditing={isEditing}
                value={form.getFieldValue("gold_adult")}
                onChange={(val) => form.setFieldsValue({ gold_adult: val })}
                min={0}
                step={1}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={isFixed ? "Child Discount" : "Child Rate"}
              name="gold_child"
            >
              <CustomNumberInput
                isFixed={isFixed}
                isPercentage={isPercentage}
                isEditing={isEditing}
                value={form.getFieldValue("gold_child")}
                onChange={(val) => form.setFieldsValue({ gold_child: val })}
                min={0}
                step={1}
              />
            </Form.Item>
          </Col>
        </Row>

        <Title level={5}>Points (Gold Only)</Title>

        {/* Earn Rate */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Earn Rate (%)"
              name="earn_rate"
              rules={[
                { required: true, message: "Please input earn rate" },
                {
                  type: "number",
                  min: 0,
                  message: "Must be a positive number",
                },
              ]}
            >
              <InputNumber
                min={0}
                step={1}
                suffix={"%"}
                parser={(value) => value.replace(/[^\d]/g, "")}
                style={{ width: 200 }}
                disabled={!isEditing}
                onKeyDown={(e) => {
                  const allowedKeys = [
                    "Backspace",
                    "Tab",
                    "ArrowLeft",
                    "ArrowRight",
                    "Delete",
                  ];
                  const isNumberKey = /^[0-9]$/.test(e.key);

                  if (!isNumberKey && !allowedKeys.includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(val) => setEarnRate(val || 0)}
              />
            </Form.Item>
            <div style={{ marginBottom: 16, color: "#555" }}>
              {earnRate}% → Spend $1, earn {(earnRate / 100).toFixed(2)} pt
            </div>
          </Col>
        </Row>

        {/* Redeem Rate */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Redeem(for Order) Rate (%)"
              name="redeem_rate"
              rules={[
                {
                  required: true,
                  message: "Please input redeem rate percentage",
                },
                {
                  type: "number",
                  min: 0,
                  message: "Must be a positive number",
                },
              ]}
            >
              <InputNumber
                min={0}
                step={1}
                suffix={"%"}
                parser={(value) => value.replace(/[^\d]/g, "")}
                style={{ width: 200 }}
                disabled={!isEditing}
                onKeyDown={(e) => {
                  const allowedKeys = [
                    "Backspace",
                    "Tab",
                    "ArrowLeft",
                    "ArrowRight",
                    "Delete",
                  ];
                  const isNumberKey = /^[0-9]$/.test(e.key);

                  if (!isNumberKey && !allowedKeys.includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(val) => setRedeemRate(val || 0)}
              />
            </Form.Item>
            <div style={{ marginBottom: 16, color: "#555" }}>
              {redeemRate}% → 100 pt = ${((redeemRate / 100) * 100).toFixed(2)}
            </div>
          </Col>
        </Row>

        {/* Cash Out Rate */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Cash Out Rate (%)"
              name="cash_out_rate"
              rules={[
                {
                  required: true,
                  message: "Please input cash out rate percentage",
                },
                {
                  type: "number",
                  min: 0,
                  message: "Must be a positive number",
                },
              ]}
            >
              <InputNumber
                min={0}
                step={1}
                suffix={"%"}
                parser={(value) => value.replace(/[^\d]/g, "")}
                style={{ width: 200 }}
                disabled={!isEditing}
                onKeyDown={(e) => {
                  const allowedKeys = [
                    "Backspace",
                    "Tab",
                    "ArrowLeft",
                    "ArrowRight",
                    "Delete",
                  ];
                  const isNumberKey = /^[0-9]$/.test(e.key);

                  if (!isNumberKey && !allowedKeys.includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(val) => setCashOutRate(val || 0)}
              />
            </Form.Item>
            <div style={{ marginBottom: 16, color: "#555" }}>
              {cashOutRate}% → 100 pt = $
              {((cashOutRate / 100) * 100).toFixed(2)}
            </div>
          </Col>
        </Row>

        {isEditing && (
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save Config and Activate
            </Button>
          </Form.Item>
        )}
      </Form>
    </Card>
  );
};

const RateConfigPage = () => {
  const [configs, setConfigs] = useState({ set1: {}, set2: {} });
  const [activeSet, setActiveSet] = useState(null);
  const [selectedSet, setSelectedSet] = useState(null);
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchConfigs = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      message.error("User not authenticated.");
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}rateConfig`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();

      if (json.success && json.data?.set1 && json.data?.set2) {
        const { set1, set2 } = json.data;
        setConfigs({ set1, set2 });

        if (set1.is_active) {
          setActiveSet("set1");
          setSelectedSet("set1");
        } else if (set2.is_active) {
          setActiveSet("set2");
          setSelectedSet("set2");
        } else {
          setActiveSet("set1");
          setSelectedSet("set1");
        }
      } else {
        message.error("Failed to fetch rate config data.");
      }
    } catch (error) {
      console.error(error);
      message.error("Error fetching rate config data.");
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleSwitchSet = (targetSet) => {
    setSelectedSet(targetSet);
  };

  const handleSubmit = (values, setKey) => {
    if (userInfo.role_id === 1) {
      return submitConfig(values, setKey); // Admin submits directly
    }

    setPendingSubmit({ values, setKey }); // Non-admin must confirm
    setShowPasswordModal(true);
  };
  const submitConfig = async (values, setKey, adminPassword = null) => {
    const token = localStorage.getItem("token");
    if (!token) {
      message.error("User not authenticated.");
      return;
    }

    const formatConfig = (config, isActive) => {
      const isPercentage = config.config_type === "percentage";
      return {
        config_type: config.config_type,
        is_active: isActive,
        basic_adult: isPercentage
          ? config.basic_adult / 100
          : config.basic_adult,
        basic_child: isPercentage
          ? config.basic_child / 100
          : config.basic_child,
        gold_adult: isPercentage ? config.gold_adult / 100 : config.gold_adult,
        gold_child: isPercentage ? config.gold_child / 100 : config.gold_child,
        earn_rate: config.earn_rate / 100,
        redeem_rate: config.redeem_rate / 100,
        cash_out_rate: config.cash_out_rate / 100,
      };
    };

    const updatedSet = formatConfig(
      {
        ...values,
        config_type: configs[setKey].config_type,
      },
      true
    );

    const otherKey = setKey === "set1" ? "set2" : "set1";

    const otherConfig = {
      ...configs[otherKey],
      is_active: false,
    };

    const payload = {
      set1: setKey === "set1" ? updatedSet : otherConfig,
      set2: setKey === "set2" ? updatedSet : otherConfig,
    };

    // If adminPassword is provided, include it in payload
    if (adminPassword) {
      payload.admin_password = adminPassword;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE_URL}rateConfig`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (json.success) {
        message.success(`Configuration for ${setKey} saved and activated`);
        await fetchConfigs();
      } else {
        message.error(json.message || "Failed to save config.");
      }
    } catch (err) {
      console.error(err);
      message.error("Error submitting config.");
    } finally {
      setSubmitting(false);
    }
  };
  const confirmAdminPassword = async () => {
    if (!adminPassword) {
      message.warning("Please enter the admin password.");
      return;
    }

    setShowPasswordModal(false);

    if (pendingSubmit) {
      await submitConfig(
        pendingSubmit.values,
        pendingSubmit.setKey,
        adminPassword
      );
      setPendingSubmit(null);
      setAdminPassword("");
    }
  };
  return (
    <div>
      <Title level={3}>Rate Configuration</Title>
      <Row gutter={24}>
        {configs.set1.config_type && (
          <Col span={12}>
            <RateConfigForm
              key={selectedSet}
              setKey="set1"
              configData={configs.set1}
              isActive={activeSet === "set1"}
              isEditing={selectedSet === "set1"}
              onSwitchSet={handleSwitchSet}
              onSubmit={handleSubmit}
            />
          </Col>
        )}
        {configs.set2.config_type && (
          <Col span={12}>
            <RateConfigForm
              key={selectedSet}
              setKey="set2"
              configData={configs.set2}
              isActive={activeSet === "set2"}
              isEditing={selectedSet === "set2"}
              onSwitchSet={handleSwitchSet}
              onSubmit={handleSubmit}
            />
          </Col>
        )}
      </Row>

      <Modal
        title="Admin Password Required"
        visible={showPasswordModal}
        onOk={confirmAdminPassword}
        onCancel={() => {
          setShowPasswordModal(false);
          setAdminPassword("");
          setPendingSubmit(null);
        }}
        confirmLoading={submitting}
        okText="Confirm"
      >
        <p>This action requires admin password confirmation.</p>
        <Input.Password
          placeholder="Enter admin password"
          value={adminPassword}
          onChange={(e) => setAdminPassword(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default RateConfigPage;
